import { useCallback, useEffect, useState } from 'react';
import PrivacyPreferenceCentre from './PrivacyPreferenceCentre';
import styles from './styles.module.scss';
import cn from 'classnames';

const CONSENT_COOKIE = '_sf_cookie_consent';
const PREFS_COOKIE = '_sf_cookie_prefs';

function getCookie(name) {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
}

function setCookie(name, value, days = 365) {
    const expires = new Date(Date.now() + days * 86400000).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
}

// Reads _sf_viewer_country to determine compliance mode.
// US → CCPA (opt-out framing), everything else → GDPR (opt-in).
function detectRegion() {
    const country = getCookie('_sf_viewer_country');
    return country === 'US' ? 'us' : 'uk';
}

const REGION_CONFIG = {
    uk: {
        description:
            'We use cookies to improve your experience, analyse website usage, and support site functionality. ' +
            'You can accept all cookies, reject non-essential cookies, or manage your preferences. ' +
            'For more information, please see our',
        suffix: null,
    },
    us: {
        description:
            'We use cookies to improve your experience, analyse website usage, and support site functionality. ' +
            'You can accept all cookies, reject non-essential cookies, or manage your preferences. ' +
            'Under California law (CCPA) you have the right to opt out of the sale of your personal information. ' +
            'For more information, please see our',
        suffix: null,
    },
};

const DEFAULT_CATEGORIES = [
    {
        key: 'functional',
        label: 'Functional Cookies',
        description: 'These cookies enable personalised features and functionality. Without them, some services may not work properly.',
    },
    {
        key: 'advertising',
        label: 'Advertising Cookies',
        description: 'Used to deliver relevant advertisements and track ad campaign performance across sites.',
    },
    {
        key: 'performance',
        label: 'Performance Cookies',
        description: 'Help us understand how visitors interact with the website by collecting and reporting anonymous analytics data.',
    },
];

export default function CookieConsent({
    cookieName = CONSENT_COOKIE,
    cookieCategories = DEFAULT_CATEGORIES,
    cookiePolicyUrl = '/cookies-policy',
    children,
}) {
    // 'banner' | 'preferences' | null
    const [view, setView] = useState(null);
    const [renderChildren, setRenderChildren] = useState(false);
    const [region, setRegion] = useState('uk');
    const [preferences, setPreferences] = useState(() =>
        Object.fromEntries(cookieCategories.map(c => [c.key, false]))
    );

    useEffect(() => {
        setRegion(detectRegion());

        const consent = getCookie(cookieName);
        if (!consent) {
            setView('banner');
        } else {
            if (consent !== 'false') {
                setRenderChildren(true);
            }
            if (consent === 'true') {
                // Legacy users accepted all but have no per-category prefs cookie yet —
                // treat as all-on so render-prop consumers load their scripts correctly.
                const allOn = Object.fromEntries(cookieCategories.map(c => [c.key, true]));
                setCookie(PREFS_COOKIE, JSON.stringify(allOn));
                setPreferences(allOn);
            } else if (consent === 'custom') {
                try {
                    const saved = JSON.parse(getCookie(PREFS_COOKIE) || '{}');
                    setPreferences(prev => ({ ...prev, ...saved }));
                } catch {}
            }
        }
    }, [cookieName]);

    const acceptAll = useCallback(() => {
        const allOn = Object.fromEntries(cookieCategories.map(c => [c.key, true]));
        setCookie(cookieName, 'true');
        setCookie(PREFS_COOKIE, JSON.stringify(allOn));
        setPreferences(allOn);
        setRenderChildren(true);
        setView(null);
    }, [cookieName, cookieCategories]);

    const rejectAll = useCallback(() => {
        const allOff = Object.fromEntries(cookieCategories.map(c => [c.key, false]));
        setCookie(cookieName, 'false');
        setCookie(PREFS_COOKIE, JSON.stringify(allOff));
        setPreferences(allOff);
        setRenderChildren(false);
        setView(null);
    }, [cookieName, cookieCategories]);

    const savePreferences = useCallback((prefs) => {
        setCookie(cookieName, 'custom');
        setCookie(PREFS_COOKIE, JSON.stringify(prefs));
        setPreferences(prefs);
        setRenderChildren(true);
        setView(null);
    }, [cookieName]);

    const regionConfig = REGION_CONFIG[region] || REGION_CONFIG.uk;

    return (
        <>
            {renderChildren && (
                typeof children === 'function' ? children(preferences) : <>{children}</>
            )}

            {view !== null && (
                <div className={styles.overlay} aria-hidden="true" />
            )}

            {view === 'banner' && (
                <div className={styles.popup} role="dialog" aria-modal="true" aria-labelledby="cookie-banner-title">
                    <h4 className={styles.popup__title} id="cookie-banner-title">
                        Your privacy matters to us
                    </h4>
                    <p className={styles.popup__text}>
                        {regionConfig.description}{' '}
                        <a href={cookiePolicyUrl} className={styles.popup__link}>Cookies Policy</a>.
                    </p>
                    <div className={styles.popup__buttons}>
                        <button
                            className={cn(styles.btn, styles['btn--customise'])}
                            onClick={() => setView('preferences')}
                        >
                            Customise
                        </button>
                        <button
                            className={cn(styles.btn, styles['btn--reject'])}
                            onClick={rejectAll}
                        >
                            Reject All
                        </button>
                        <button
                            className={cn(styles.btn, styles['btn--accept'])}
                            onClick={acceptAll}
                        >
                            Accept All
                        </button>
                    </div>
                </div>
            )}

            {view === 'preferences' && (
                <PrivacyPreferenceCentre
                    categories={cookieCategories}
                    preferences={preferences}
                    onSave={savePreferences}
                    onAcceptAll={acceptAll}
                    onClose={() => setView('banner')}
                />
            )}
        </>
    );
}
